import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { SupportStore } from '@core/services/support/support.store';
import { SupportContextService } from '@core/services/support/support-context.service';
import { AuthStore } from '@core/services/auth/auth.store';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { UIFeedbackService } from '@shared/services/ui-feedback.service';
import {
  SUPPORT_LIMITS,
  type SupportAudience,
  type SupportLang,
  type SupportMessage,
  type SupportThread
} from '@shared/definitions/support';

/**
 * The support conversation, as a tab inside the docked panel.
 *
 * Two screens, not a route: a thread list and one thread. The panel is shared
 * with the assistant, so this component never opens or closes it — it only
 * renders inside it. That is why there is no dialog anywhere here: DESIGN.md
 * §13.7 reserves dialogs for something the user just asked for, and a support
 * answer arriving is not a gesture the user made.
 *
 * The composer intentionally keeps its text on failure (DESIGN.md §13): a
 * support message is the one thing a frustrated user least wants to retype.
 */
@Component({
  selector: 'app-support-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    DatePipe,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslatePipe
  ],
  templateUrl: './support-panel.component.html',
  styleUrls: ['./support-panel.component.scss']
})
export class SupportPanelComponent {
  private readonly store = inject(SupportStore);
  private readonly contextService = inject(SupportContextService);
  private readonly authStore = inject(AuthStore);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly translate = inject(TranslateService);
  private readonly feedback = inject(UIFeedbackService);

  readonly threads = this.store.threads;
  readonly messages = this.store.messages;
  readonly activeThread = this.store.activeThread;
  readonly sending = this.store.sending;
  readonly loadingMessages = this.store.loadingMessages;
  readonly hasThreads = this.store.hasThreads;

  readonly limits = SUPPORT_LIMITS;

  /** `list` until a thread is picked, then `thread`; `compose` for a new one. */
  readonly view = computed<'list' | 'thread' | 'compose'>(() => {
    if (this.composing()) return 'compose';
    return this.activeThread() ? 'thread' : 'list';
  });

  readonly composing = signal(false);
  readonly newSubject = signal('');
  readonly newMessage = signal('');
  readonly reply = signal('');
  /** Ticked by default — the product decision. Untick drops the whole object. */
  readonly attachContext = signal(true);
  readonly contextPreviewOpen = signal(false);
  readonly contextPreview = signal<string>('');
  /** Per-message translation, keyed by message id. Never persisted client-side. */
  readonly translations = signal<Record<string, string>>({});
  readonly translatingId = signal<string | null>(null);

  readonly canSubmitNew = computed(
    () =>
      !this.sending() &&
      this.newSubject().trim().length > 0 &&
      this.newMessage().trim().length > 0
  );

  readonly canReply = computed(() => !this.sending() && this.reply().trim().length > 0);

  /** Set while a ticket carries an assistant transcript (#291). */
  readonly handoffDraft = this.store.handoffDraft;

  /**
   * A tenant can address their MANAGER as well as Proporio (#292).
   *
   * The choice is only offered where it exists. For everyone else there is one
   * possible recipient, and a picker with a single option is a question that
   * answers itself.
   */
  readonly canChooseAudience = computed(
    () => this.workspaceStore.currentWorkspace()?.type === 'tenant'
  );

  readonly audience = signal<SupportAudience>('proporio');

  constructor() {
    // The handoff opens the compose form pre-filled rather than sending on the
    // user's behalf: they asked to talk to a human, which is a request to
    // write, not a message. See `SupportStore.startHandoff`.
    effect(() => {
      const draft = this.handoffDraft();
      if (!draft) return;
      this.composing.set(true);
      this.contextPreviewOpen.set(false);
      if (!this.newSubject().trim()) {
        this.newSubject.set(
          draft.subject || this.translate.instant('support.handoff.defaultSubject')
        );
      }
    });
  }

  // -------------------------------------------------------------------------
  // Navigation inside the tab
  // -------------------------------------------------------------------------

  openThread(thread: SupportThread): void {
    this.composing.set(false);
    this.translations.set({});
    this.store.selectThread(thread.id);
  }

  backToList(): void {
    this.composing.set(false);
    this.store.clearHandoff();
    this.store.selectThread(null);
  }

  async startCompose(): Promise<void> {
    this.store.selectThread(null);
    this.composing.set(true);
    this.contextPreviewOpen.set(false);
    // Captured up front so the preview shows exactly what will be sent, not a
    // snapshot taken later from a different route.
    const context = await this.contextService.capture();
    this.contextPreview.set(JSON.stringify(context, null, 2));
  }

  toggleContextPreview(): void {
    this.contextPreviewOpen.update(open => !open);
  }

  // -------------------------------------------------------------------------
  // Writes
  // -------------------------------------------------------------------------

  async submitNew(): Promise<void> {
    if (!this.canSubmitNew()) return;
    const user = this.authStore.user();
    const draft = this.handoffDraft();
    const context = this.attachContext()
      ? await this.contextService.capture(
        draft?.conversationId ? { conversationId: draft.conversationId } : undefined
      )
      : null;

    const audience = this.canChooseAudience() ? this.audience() : 'proporio';
    const result = await this.store.createThread({
      subject: this.newSubject().trim(),
      message: this.newMessage().trim(),
      displayName: user?.displayName || undefined,
      locale: this.currentLang(),
      context,
      // The transcript rides the SAME consent as the technical context: both
      // can carry a tenant's name or an amount, and the user ticked one box.
      ...(draft && this.attachContext() ? { systemNote: draft.transcript } : {}),
      audience,
      // Required by the server for a workspace-addressed ticket, and verified
      // there — it re-checks membership rather than trusting this.
      ...(audience === 'workspace'
        ? { workspaceId: this.workspaceStore.currentWorkspace()?.workspaceId }
        : {})
    });

    if (!result) {
      // The draft is deliberately left in place — see the class doc.
      this.feedback.toast.error(this.translate.instant('support.errors.createFailed'));
      return;
    }

    this.newSubject.set('');
    this.newMessage.set('');
    this.composing.set(false);
    this.store.clearHandoff();
    this.feedback.toast.success(this.translate.instant('support.toast.created'));
  }

  async submitReply(): Promise<void> {
    const thread = this.activeThread();
    if (!thread || !this.canReply()) return;
    const ok = await this.store.reply(thread.id, this.reply());
    if (!ok) {
      this.feedback.toast.error(this.translate.instant('support.errors.replyFailed'));
      return;
    }
    this.reply.set('');
  }

  async closeThread(): Promise<void> {
    const thread = this.activeThread();
    if (!thread) return;
    const confirmed = await this.feedback.confirm.basic({
      title: this.translate.instant('support.close.title'),
      message: this.translate.instant('support.close.message'),
      confirmText: this.translate.instant('support.close.confirm')
    });
    if (!confirmed) return;
    await this.store.closeThread(thread.id);
  }

  /**
   * Translate one message into the reader's current UI language.
   *
   * On demand rather than automatic: most fils are already in the right
   * language, and every translation is a paid model call.
   */
  async translateMessage(message: SupportMessage): Promise<void> {
    const thread = this.activeThread();
    if (!thread) return;
    const existing = this.translations()[message.id];
    if (existing) {
      // Second click = show the original again.
      this.translations.update(map => {
        const next = { ...map };
        delete next[message.id];
        return next;
      });
      return;
    }
    this.translatingId.set(message.id);
    const text = await this.store.translate(thread.id, message.id, this.currentLang());
    this.translatingId.set(null);
    if (text) {
      this.translations.update(map => ({ ...map, [message.id]: text }));
    } else {
      this.feedback.toast.error(this.translate.instant('support.errors.translateFailed'));
    }
  }

  // -------------------------------------------------------------------------
  // Presentation
  // -------------------------------------------------------------------------

  displayedContent(message: SupportMessage): string {
    return this.translations()[message.id] ?? message.content;
  }

  isTranslated(message: SupportMessage): boolean {
    return !!this.translations()[message.id];
  }

  statusLabel(thread: SupportThread): string {
    return `support.status.${thread.status}`;
  }

  /**
   * The reader's language, constrained to what the translator supports.
   *
   * @returns A supported language code.
   */
  private currentLang(): SupportLang {
    const lang = (this.translate.currentLang() || this.translate.getFallbackLang() || 'en').slice(0, 2);
    return (['en', 'fr', 'nl', 'de'] as const).includes(lang as SupportLang)
      ? (lang as SupportLang)
      : 'en';
  }
}
